import React, { useMemo } from "react"
import {
  BadgeCheck,
  Ban,
  CalendarClock,
  CreditCard,
  Package,
  MessageCircle,
  Receipt,
  ShoppingBag,
  UtensilsCrossed,
} from "lucide-react"
import { EOrderStatusType } from "../../Enum/EOrderStatus"
import { normalizeReservationDateForInput } from "../../Extensions/ReservationExtensions"
import { EAppView } from "../../Enum/EAppViewType"
import { usePublicViewContext } from "../../context/usePublicViewContext"
import { useTranslation } from "react-i18next"
import { LoginRedirectView } from "../Components/LoginRedirectView"
import { companyMemory, orderMemory } from "../../Objects/Memory/Memory"
import { useLoad } from "../../context/useLoadContext"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { LoadingComponet } from "../Components/LoadingComponet"
import { PaymentDetails } from "../Components/PaymentDetails"
import { getWhatsappSupportLink } from "../Helpers/Validators"
import { getPublicMenuTheme, isLegacyPublicMenuTheme } from "../Helpers/PublicMenuTheme"

interface OrderHistoryViewProps {
  setCurrentView: (view: EAppView) => void
}

export const OrderHistoryView: React.FC<OrderHistoryViewProps> = ({ setCurrentView }) => {
  const { t } = useTranslation()

  const { auth } = usePublicViewContext()

  const { isFlagLoading } = useLoad()

  const publicMenuTheme = getPublicMenuTheme(companyMemory)

  const isLegacyTheme = isLegacyPublicMenuTheme(publicMenuTheme)

  const whatsappPhone = companyMemory.companySocialMediaEntity?.whatsAppPhone?.replace(/\D/g, "") ?? ""

  const hasValidWhatsappPhone = whatsappPhone.length >= 12 && whatsappPhone.length <= 13

  const finishedOrders = useMemo(() => {
    return orderMemory.listData.filter(
      (order) => order.eOrderStatusType === EOrderStatusType.Delivered || order.isCancel,
    )
  }, [orderMemory.listData])

  if (!auth.customer) {
    return (
      <LoginRedirectView
        title={t("publicMenu:history.title")}
        description={t("publicMenu:history.description")}
        setCurrentView={setCurrentView}
      />
    )
  }

  if (isFlagLoading) {
    return <LoadingComponet />
  }

  if (finishedOrders.length === 0) {
    return (
      <Animate type={AnimationTypes.fadeInTraslate}>
        <div className="mx-auto flex max-w-xl flex-col items-center space-y-8 px-4 py-16 text-center animate-in fade-in duration-700 sm:py-24">
          <div className="flex h-28 w-28 items-center justify-center rounded-xl bg-gradient-to-br from-orange-100 via-white to-amber-50 text-orange-500 shadow-sm sm:h-32 sm:w-32">
            <Receipt size={48} />
          </div>

          <div className="space-y-3">
            <h2
              className="text-2xl font-black tracking-tight text-gray-900"
              style={isLegacyTheme ? undefined : { color: publicMenuTheme.emptyStateTitleColor }}
            >
              {t("publicMenu:history.empty_title")}
            </h2>

            <p
              className="mx-auto max-w-sm font-medium text-gray-500"
              style={isLegacyTheme ? undefined : { color: publicMenuTheme.emptyStateDescriptionColor }}
            >
              {t("publicMenu:history.empty_description")}
            </p>
          </div>
        </div>
      </Animate>
    )
  }

  return (
    <Animate type={AnimationTypes.fadeInTraslate}>
      <div className="mx-auto max-w-7xl rounded-md pb-10">
        <div className="space-y-6 px-3 sm:px-4">
          <div className="overflow-hidden rounded-xl border-2 border-orange-200 bg-gradient-to-r from-white via-orange-50 to-orange-100 px-4 py-5 shadow-sm sm:px-5">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-3">
                <span className="inline-flex items-center gap-2 rounded-lg bg-orange-100 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-orange-600">
                  <ShoppingBag size={14} />
                  {t("publicMenu:history.consolidated_history")}
                </span>
                <div>
                  <h2 className="text-2xl font-black uppercase tracking-tight text-slate-900 sm:text-3xl">
                    {t("publicMenu:history.heading")}
                  </h2>
                  <p className="mt-2 max-w-2xl text-sm font-medium text-slate-500 sm:text-base">
                    {t("publicMenu:history.heading_description")}
                  </p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3 lg:min-w-[420px]">
                <div className="rounded-xl border-2 border-orange-200 bg-white px-4 py-3 text-slate-800 shadow-sm">
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                    {t("publicMenu:history.finished_orders")}
                  </p>
                  <p className="mt-1 text-2xl font-black text-orange-600">{finishedOrders.length}</p>
                </div>
                <div className="rounded-xl border-2 border-orange-200 bg-white px-4 py-3 text-slate-800 shadow-sm">
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                    {t("publicMenu:history.delivered")}
                  </p>
                  <p className="mt-1 text-2xl font-black text-emerald-600">
                    {finishedOrders.filter((order) => !order.isCancel).length}
                  </p>
                </div>
                <div className="rounded-xl border-2 border-orange-200 bg-white px-4 py-3 text-slate-800 shadow-sm">
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                    {t("publicMenu:history.cancelled")}
                  </p>
                  <p className="mt-1 text-2xl font-black text-rose-500">
                    {finishedOrders.filter((order) => order.isCancel).length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {finishedOrders.map((order) => {
            const items = [...order.orderMeals, ...order.orderDrinks]
            const totalItems = items.length
            const totalAdditionalValue = items.reduce((sum, item) => sum + item.getValueAddtions(), 0)
            const totalDiscountValue = items.reduce((sum, item) => sum + item.valueDiscount, 0)
            const statusLabel = order.isCancel
              ? t("publicMenu:history.status_cancelled")
              : t("publicMenu:history.status_finished")
            const statusClasses = order.isCancel
              ? "border-rose-200 bg-rose-50 text-rose-600"
              : "border-emerald-200 bg-emerald-50 text-emerald-600"
            const StatusIcon = order.isCancel ? Ban : BadgeCheck
            const PlaceIcon = order.eOrderPlaceType.toOrderPlaceTypeString().toLowerCase().includes("delivery")
              ? Package
              : UtensilsCrossed

            return (
              <Animate key={order.id} type={AnimationTypes.fadeInTraslate}>
                <div className="overflow-hidden rounded-xl border-2 border-orange-200 bg-white shadow-[0_30px_80px_-45px_rgba(15,23,42,0.35)] ring-1 ring-black/5 animate-in slide-in-from-bottom-6 duration-500">
                  <div className="border-b border-orange-200 bg-gradient-to-r from-white via-orange-50 to-orange-100 p-4 text-slate-800 md:p-6">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                      <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-2 text-[10px] font-black uppercase tracking-[0.28em] text-orange-600">
                          <span className="rounded-lg border border-orange-200 bg-white px-3 py-1.5 shadow-sm">
                            {t("publicMenu:history.protocol")} #{order.id}
                          </span>
                          <span className="rounded-lg border border-orange-200 bg-white px-3 py-1.5 text-slate-500 shadow-sm">
                            {order.date.toReadableDate()}
                          </span>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-orange-200 bg-white text-orange-500 shadow-sm">
                              <PlaceIcon size={22} />
                            </div>

                            <div>
                              <h3 className="text-2xl font-black uppercase tracking-tight sm:text-3xl">
                                {order.eOrderPlaceType.toOrderPlaceTypeString()}
                              </h3>
                              <p className="text-sm font-medium text-slate-500">
                                {t("publicMenu:history.order_summary_description")}
                              </p>
                            </div>
                          </div>

                          <div className="grid gap-3 sm:grid-cols-3">
                            <div className="rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white px-4 py-3 shadow-sm">
                              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">
                                {t("publicMenu:tracking.items")}
                              </p>
                              <p className="mt-1 text-lg font-black text-slate-800">{totalItems}</p>
                            </div>
                            <div className="rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white px-4 py-3 shadow-sm">
                              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">
                                {t("publicMenu:tracking.additionals")}
                              </p>
                              <p className="mt-1 text-lg font-black text-slate-800">
                                R$ {totalAdditionalValue.toFixed(2)}
                              </p>
                            </div>
                            <div className="rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white px-4 py-3 shadow-sm">
                              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">
                                {t("publicMenu:tracking.discounts")}
                              </p>
                              <p className="mt-1 text-lg font-black text-slate-800">
                                R$ {totalDiscountValue.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 lg:max-w-xs lg:min-w-[250px]">
                        <div
                          className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 shadow-sm ${statusClasses}`}
                        >
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-current/20 bg-white/80 text-sm shadow-sm">
                            <StatusIcon size={18} />
                          </div>
                          <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-current/80">
                              {t("publicMenu:tracking.current_status")}
                            </p>
                            <p className="text-sm font-black uppercase tracking-wide">{statusLabel}</p>
                          </div>
                        </div>

                        <div className="rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white p-4 text-slate-800 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-orange-200 bg-white text-orange-500">
                              <CalendarClock size={18} />
                            </div>
                            <div>
                              <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">
                                {t("publicMenu:history.closing")}
                              </p>
                              <h4 className="text-base font-black">{t("publicMenu:history.order_archived")}</h4>
                            </div>
                          </div>

                          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                            <div className="rounded-xl border-2 border-orange-200 bg-white p-4">
                              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">
                                {t("publicMenu:history.date")}
                              </p>
                              <p className="mt-2 text-sm font-bold text-slate-700">{order.date.toReadableDate()}</p>
                            </div>
                            <div className="rounded-xl border-2 border-orange-200 bg-white p-4">
                              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">
                                {t("publicMenu:history.total_final")}
                              </p>
                              <p className="mt-2 text-2xl font-black tracking-tight text-orange-600">
                                R$ {order.orderPaymentEntity.totalFinal.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {hasValidWhatsappPhone && (
                          <a
                            href={getWhatsappSupportLink(order.id, whatsappPhone)}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-center gap-2 rounded-xl border-2 border-emerald-400 bg-white px-4 py-3 text-sm font-black text-black shadow-sm transition hover:bg-emerald-600"
                            data-interaction-description-key="interactionDescriptions:publicMenu.pages.orderHistoryView.link.askWhatsapp"
                          >
                            <MessageCircle size={16} />
                            {t("publicMenu:history.ask_on_whatsapp")}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-5 p-4 md:p-6 ">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
                          <ShoppingBag size={18} />
                        </div>
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-[0.24em] text-orange-500">
                            {t("publicMenu:tracking.order_details")}
                          </p>
                          <h4 className="text-lg font-black text-slate-900">
                            {t("publicMenu:history.finished_items")}
                          </h4>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {items.map((item, idx) => {
                          return (
                            <div
                              key={item.id}
                              className="group rounded-xl border border-slate-100 bg-gradient-to-br from-white to-slate-50/80 p-4 shadow-sm transition hover:border-orange-200 hover:shadow-md"
                            >
                              <div className="flex flex-col gap-4 sm:flex-row">
                                <img
                                  src={item.getImage()}
                                  className="h-20 w-20 rounded-lg object-cover shadow-md sm:h-24 sm:w-24"
                                />

                                <div className="min-w-0 flex-1 space-y-3">
                                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                    <div className="space-y-1">
                                      <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">
                                        {t("publicMenu:tracking.item_number", { number: idx + 1 })}
                                      </span>
                                      <h5 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">
                                        {item.getName()}
                                      </h5>
                                    </div>

                                    <div className="rounded-xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white px-4 py-3 text-left text-slate-800 shadow-sm sm:min-w-[140px] sm:text-right">
                                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                                        {t("publicMenu:tracking.item_total")}
                                      </p>
                                      <p className="mt-1 text-lg font-black text-orange-600">
                                        R$ {item.getValueWithAddtions().toFixed(2)}
                                      </p>
                                    </div>
                                  </div>

                                  {item.isReservation && (
                                    <p className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-700">
                                      {t("publicMenu:tracking.reservation_summary", {
                                        date: `${normalizeReservationDateForInput(item.reservationDate)}T00:00:00`.toReadableDate(
                                          "pt-BR",
                                          true,
                                        ),
                                        time: item.reservationTime,
                                      })}
                                    </p>
                                  )}

                                  {item.description && (
                                    <p className="rounded-xl border border-slate-100 bg-white px-4 py-3 text-sm font-medium italic text-slate-500">
                                      “{item.description}”
                                    </p>
                                  )}

                                  {item.addtionals.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                      {item.addtionals.map((add) => (
                                        <div
                                          key={add.id}
                                          className="flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5"
                                        >
                                          <span className="text-[10px] font-black uppercase tracking-[0.15em] text-orange-700">
                                            + {add.addtionalEntity.name} x{add.amount}
                                          </span>
                                          <span className="text-[11px] font-bold text-orange-500">
                                            R$ {add.valueTotal.toFixed(2)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  <div className="grid gap-2 text-sm font-semibold text-slate-500 sm:grid-cols-2 xl:grid-cols-4">
                                    <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-slate-100">
                                      <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                                        {t("publicMenu:history.base")}
                                      </span>
                                      <span className="text-slate-700">R$ {item.valueBase.toFixed(2)}</span>
                                    </div>
                                    <div className="rounded-lg bg-orange-50 px-3 py-2 ring-1 ring-orange-100">
                                      <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-orange-400">
                                        {t("publicMenu:tracking.additionals")}
                                      </span>
                                      <span className="text-orange-700">+ R$ {item.getValueAddtions().toFixed(2)}</span>
                                    </div>
                                    <div className="rounded-lg bg-rose-50 px-3 py-2 ring-1 ring-rose-100">
                                      <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-rose-400">
                                        {t("publicMenu:history.discount")}
                                      </span>
                                      <span className="text-rose-700">- R$ {item.valueDiscount.toFixed(2)}</span>
                                    </div>
                                    <div className="rounded-lg bg-slate-100 px-3 py-2 text-gray-800 ring-1 ring-slate-300">
                                      <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                                        {t("publicMenu:tracking.subtotal")}
                                      </span>
                                      <span>R$ {item.getValueWithAddtions().toFixed(2)}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="rounded-xl border border-slate-100 bg-gradient-to-br from-white via-orange-50/40 to-white p-4 text-slate-800 border-2 border-orange-200 shadow-sm md:p-6">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-orange-200 bg-white text-orange-500">
                            <CreditCard size={18} />
                          </div>
                          <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-500">
                              {t("publicMenu:tracking.financial_summary")}
                            </p>
                            <h4 className="text-base font-black">{t("publicMenu:history.payment_and_closing")}</h4>
                          </div>
                        </div>

                        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                          <div className="rounded-xl border-2 border-orange-200 bg-white p-4">
                            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">
                              {t("publicMenu:history.payment_method")}
                            </p>
                            <p className="mt-2 text-sm font-bold text-slate-700">
                              {order.orderPaymentEntity.ePaymentType.toPaymentTypeString()}
                            </p>
                          </div>
                          <div className="rounded-xl border-2 border-orange-200 bg-white p-4">
                            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">
                              {t("publicMenu:history.total_final")}
                            </p>
                            <p className="mt-2 text-2xl font-black tracking-tight text-orange-600">
                              R$ {order.orderPaymentEntity.totalFinal.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="overflow-hidden rounded-xl border-2 border-orange-200 bg-white shadow-sm">
                        <div className="border-b border-slate-100 px-5 py-4">
                          <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">
                            {t("publicMenu:tracking.payment")}
                          </p>
                          <h4 className="mt-1 text-base font-black text-slate-900">
                            {t("publicMenu:tracking.payment_details")}
                          </h4>
                        </div>
                        <div className="p-2 sm:p-4">
                          <PaymentDetails order={order} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Animate>
            )
          })}
        </div>
      </div>
    </Animate>
  )
}
